import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import Breadcrumbs from '../components/Breadcrumbs';
import ChapterBreak from '../components/ChapterBreak';

const A = 'https://customer-assets-gfyr7b9c.emergentagent.net/job_modularoom-design/artifacts/';
const API = process.env.REACT_APP_BACKEND_URL;

const FEATURE = {
  tag: 'Hospitality & Architecture',
  title: 'CS25: A Capsule House for Resort Hospitality in India',
  excerpt: "Meet Modularoom's hospitality-focused Capsule Suite. One bedroom, one bathroom and a private balcony — designed as a repeatable accommodation product for resorts, retreats and destination hospitality.",
  image: `${A}su1kq50b_Capsule%20House%20MODULAROOM%20CS25%20Exterior.jpeg`,
  to: '/journal/cs25-capsule-house-india-resorts',
  meta: 'Read · 12 min · February 2026',
};

const UPCOMING = [
  { tag:'Design Notes', title:'Why we design architecture as a product.', excerpt:'The distinction between building and product, and why our factory floor sets the tone for every finished suite.', image: `${A}5b5mzuzp_Gemini%20CS37%203.png` },
  { tag:'Field Report', title:'A CS33 on the cliffside: a case study.', excerpt:'How a Capsule Suite was engineered, transported and installed above the ocean.', image: `${A}qalnrf4h_d1498605-415f-432c-8527-70a729ccf41a.jpeg` },
  { tag:'Studio Journal', title:'The material palette of a Studio Suite.', excerpt:'Warm timbers, matte finishes and hospitality-grade fittings inside the SS35.', image: `${A}sk989pgq_Gemini%20SS35%201%281%29.webp` },
];

export default function Journal() {
  return (
    <main data-testid="journal-page" className="bg-white text-[#111]">
      <Helmet>
        <title>Journal | MODULAROOM</title>
        <meta name="description" content="Design thinking, engineering notes and field stories from MODULAROOM projects. Insights into luxury modular homes, capsule suites and off-site construction." />
        <link rel="canonical" href="https://modularoom.com/journal" />
      </Helmet>

      <section className="pt-40 md:pt-56 pb-16 max-w-[1440px] mx-auto px-6 md:px-12">
        <Breadcrumbs items={[{label:'Home', to:'/'}, {label:'Explore'}, {label:'Journal'}]} />
        <h1 className="mt-14 font-display text-6xl md:text-7xl lg:text-8xl tracking-tight leading-[0.95] max-w-4xl">
          Journal.<br/><span className="italic text-black/40">Field notes from the studio.</span>
        </h1>
        <p className="mt-10 max-w-xl text-[17px] text-black/60 leading-[1.75]">
          Design thinking, engineering discoveries, and stories from the sites where MODULAROOM comes to life.
        </p>
      </section>

      {/* Featured article — CS25 flagship */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A] mb-8">Featured</div>
          <Link
            to={FEATURE.to}
            data-testid="journal-featured-cs25"
            className="group grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-14 items-center"
          >
            <div className="md:col-span-7">
              <div className="relative aspect-[4/3] overflow-hidden bg-black">
                <img
                  src={FEATURE.image}
                  alt="Modularoom CS25 Capsule Suite exterior — a capsule house for resort hospitality in India"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.04]"
                />
              </div>
            </div>
            <div className="md:col-span-5">
              <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A]">{FEATURE.tag}</div>
              <h2 className="mt-4 font-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                {FEATURE.title}
              </h2>
              <p className="mt-6 text-[15px] md:text-[16px] leading-[1.8] text-black/65 max-w-lg">
                {FEATURE.excerpt}
              </p>
              <div className="mt-8 flex items-center gap-4 text-[11px] tracking-[0.22em] uppercase text-black/45">
                <span>{FEATURE.meta}</span>
              </div>
              <span className="mt-8 inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-black/85 group-hover:text-[#C7A36A] transition-colors">
                Read the article →
              </span>
            </div>
          </Link>
        </div>
      </section>

      <ChapterBreak src={UPCOMING[1].image} chapter="Field notes" minHeight="55svh" />

      <section className="py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A] mb-10">More from the Journal</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {UPCOMING.map((s) => (
              <article key={s.title} className="group cursor-default">
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img src={s.image} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.045]" />
                </div>
                <div className="mt-6 text-[10px] tracking-[0.32em] uppercase text-[#C7A36A]">{s.tag}</div>
                <h3 className="mt-3 font-display text-2xl md:text-3xl tracking-tight leading-[1.1]">{s.title}</h3>
                <p className="mt-3 text-black/60 text-sm leading-relaxed">{s.excerpt}</p>
                <div className="mt-5 text-[11px] tracking-[0.24em] uppercase text-black/40">Full story coming soon</div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F5F5F5] py-24" id="newsletter">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-10 md:gap-16 items-end">
          <div>
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A] mb-4">Modularoom Journal</div>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-[1.1] max-w-lg">
              Receive the next chapter<br/>
              <span className="italic text-black/40">of the Journal.</span>
            </h2>
            <p className="mt-5 max-w-md text-[14px] text-black/60 leading-relaxed">
              Field notes, product releases and long-form features on modular hospitality — delivered when we publish. Unsubscribe anytime.
            </p>
          </div>
          <NewsletterForm />
        </div>
      </section>
    </main>
  );
}

function NewsletterForm() {
  const [email, setEmail] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | done

  const submit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!value || !/^\S+@\S+\.\S+$/.test(value)) {
      toast.error('Please enter a valid email address.');
      return;
    }
    setStatus('sending');
    try {
      const res = await fetch(`${API}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: value, source: 'journal', honeypot }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setStatus('done');
      toast.success(data.already_subscribed
        ? "You're already on the list — thank you."
        : "You're in. The next chapter will arrive in your inbox.");
    } catch (err) {
      setStatus('idle');
      toast.error(err.message || 'Could not subscribe. Please try again.');
    }
  };

  if (status === 'done') {
    return (
      <div data-testid="newsletter-success" className="md:justify-self-end w-full md:max-w-md">
        <div className="border-t border-black/15 pt-6">
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A]">Subscribed</div>
          <p className="mt-3 text-[14px] leading-relaxed text-black/70">
            You&apos;ll hear from us when the next chapter of the Modularoom Journal is published.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="newsletter-form"
      className="md:justify-self-end w-full md:max-w-md"
      aria-label="Subscribe to the Modularoom Journal"
    >
      <label htmlFor="newsletter-email" className="text-[10px] tracking-[0.28em] uppercase text-black/45">
        Email address
      </label>
      <div className="mt-3 flex items-center gap-3 border-b border-black/25 focus-within:border-[#C7A36A] transition-colors">
        <input
          id="newsletter-email"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          data-testid="newsletter-email-input"
          className="w-full bg-transparent py-3 text-[15px] focus:outline-none placeholder:text-black/30"
        />
        <button
          type="submit"
          disabled={status === 'sending'}
          data-testid="newsletter-submit"
          className="shrink-0 inline-flex items-center gap-2 rounded-full bg-[#111] text-white px-6 py-3 text-[10px] tracking-[0.24em] uppercase hover:bg-[#C7A36A] transition-colors disabled:opacity-50"
        >
          {status === 'sending' ? 'Subscribing…' : 'Subscribe →'}
        </button>
      </div>
      {/* honeypot — hidden from users, visible to bots */}
      <input
        type="text"
        tabIndex="-1"
        autoComplete="off"
        aria-hidden="true"
        value={honeypot}
        onChange={(e) => setHoneypot(e.target.value)}
        className="hidden"
      />
      <p className="mt-4 text-[11px] tracking-[0.06em] text-black/45 leading-relaxed">
        No spam. Only new Journal articles. Unsubscribe anytime.
      </p>
    </form>
  );
}
